/**
 * The controller page's Sensors tab: each node's sensor device and its channels.
 * Split out of pages/irrigation/EspNowControllerDetail.tsx.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Radio,
  RefreshCw,
  AlertCircle,
  Plus,
  Trash2,
  Activity,
  Sprout,
  Thermometer,
  Droplets,
  Waves,
  ToggleLeft,
  Pencil,
} from 'lucide-react';
import { useOrgTimezone, formatTimeInTz } from '@/utils/timezone';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import espnowService, { type EspNowRouter, type ControllerSensorData } from '../../../services/espnowIrrigation';
import sensorDevicesService, { type SensorType, type SensorChannel } from '../../../services/sensorDevices';
import { useModuleLicense } from '../../../hooks/useModuleLicense';

// ─── Sensors Tab ─────────────────────────────────────────────────────────────

const CHANNEL_TYPE_ICONS: Record<string, React.ReactNode> = {
  temperature: <Thermometer className="w-4 h-4 text-orange-500" />,
  humidity: <Droplets className="w-4 h-4 text-sky-500" />,
  water_level: <Waves className="w-4 h-4 text-blue-500" />,
  tank_level: <Droplets className="w-4 h-4 text-blue-600" />,
  digital_input: <ToggleLeft className="w-4 h-4 text-gray-500" />,
  generic_adc: <Activity className="w-4 h-4 text-violet-500" />,
  soil_tension: <Sprout className="w-4 h-4 text-green-600" />,
};

// How a channel's raw value becomes its reading (sensorDeviceService.calibrateValue).
type Calibration = 'none' | 'linear' | 'watermark';

// Where a Watermark's soil temperature comes from (HHNodeServer
// soilTemperatureService). Open-Meteo models four depths; the typed figure is
// what a channel set to it falls back on when the model cannot be read.
type SoilTempSource = 'open_meteo' | 'fixed';
const SOIL_TEMP_DEPTHS: Array<{ cm: string; label: string }> = [
  { cm: '0', label: 'Surface (0 cm)' },
  { cm: '6', label: '6 cm' },
  { cm: '18', label: '18 cm' },
  { cm: '54', label: '54 cm' },
];

function soilTempNote(raw: Record<string, unknown> | null | undefined): string | null {
  const c = Number(raw?.soil_temp_c);
  if (raw?.soil_temp_c == null || !Number.isFinite(c)) return null;
  switch (raw.soil_temp_source) {
    case 'open_meteo': return `at ${c.toFixed(1)} °C, Open-Meteo ${String(raw.soil_temp_depth_cm)} cm`;
    case 'fixed_fallback':
    case 'default_fallback': return `at ${c.toFixed(1)} °C, Open-Meteo unavailable`;
    default: return `at ${c.toFixed(1)} °C`;
  }
}

/**
 * A node in the mesh that can carry sensor channels: the master controller's own
 * on-board inputs, or any remote router.
 *
 * `routerMac` is the *addressing* key used by every sensor endpoint, and it is
 * deliberately null for the master — the master's own inputs are stored with
 * field_node_mac IS NULL, even though its espnow_routers row carries its MAC.
 */
interface SensorNode {
  key: string;
  routerMac: string | null;
  label: string;
  sublabel: string;
  online: boolean;
  /**
   * Reachability verdict, when the node has one. `undefined` for the rows this
   * screen invents (the master's own inputs, a configured device whose node has
   * gone from the registry) - those are not nodes with a radio to be reached.
   */
  state?: 'online' | 'offline' | 'unknown' | null;
  isLocal: boolean;
  /** null when the node hasn't reported capabilities (pre-capability firmware). */
  diCount: number | null;
  adcCount: number | null;
}

function buildSensorNodes(
  routers: EspNowRouter[],
  devices: ControllerSensorData[]
): SensorNode[] {
  const nodes: SensorNode[] = routers.map(r => {
    const isLocal = r.is_local === true;
    return {
      key: r.router_mac,
      routerMac: isLocal ? null : r.router_mac,
      label: isLocal
        ? 'Master (on-board inputs)'
        : (r.name || r.router_mac.slice(-8)),
      sublabel: r.device_type || r.router_mac,
      online: r.online,
      state: r.state ?? null,
      isLocal,
      diCount: r.di_count ?? null,
      adcCount: r.adc_count ?? null,
    };
  });

  // The master's own inputs are always configurable, so make sure there is a
  // node for them even when the master hasn't published a local row — a master
  // with no relays didn't publish one at all on older firmware, and its existing
  // sensor config would otherwise be unreachable from this screen.
  if (!nodes.some(n => n.isLocal)) {
    nodes.unshift({
      key: '__master__',
      routerMac: null,
      label: 'Master (on-board inputs)',
      sublabel: 'Controller',
      online: true,
      isLocal: true,
      diCount: null,
      adcCount: null,
    });
  }

  // Surface any configured device whose node is missing from the router list
  // (router removed from the registry, re-flashed, or temporarily dropped out)
  // rather than silently hiding its channels and readings.
  for (const d of devices) {
    const mac = d.field_node_mac ?? null;
    if (!mac || nodes.some(n => n.routerMac === mac)) continue;
    nodes.push({
      key: mac,
      routerMac: mac,
      label: d.router_name || d.name || mac.slice(-8),
      sublabel: `${mac} · not currently reporting`,
      online: false,
      state: 'unknown',
      isLocal: false,
      diCount: null,
      adcCount: null,
    });
  }

  return nodes;
}

export function SensorsTab({
  controllerId,
  orgId: _orgId,
  routers,
}: {
  controllerId: string;
  orgId: number | null;
  routers: EspNowRouter[];
}) {
  const sensorTz = useOrgTimezone();
  // Sensor-device setup and channel config are admin-only within irrigation.
  const { hasAccess: canIrrigationAdmin } = useModuleLicense('irrigation', 'admin');

  // null = the master's own inputs, which is also the safe default when the
  // router list hasn't loaded yet.
  const [selectedMac, setSelectedMac] = useState<string | null>(null);
  // Every sensor device on this controller, keyed by node. One request serves
  // both the node badges and the selected node's channels.
  const [devices, setDevices] = useState<ControllerSensorData[]>([]);
  const sensorNodes = useMemo(() => buildSensorNodes(routers, devices), [routers, devices]);
  const [notSetUp, setNotSetUp] = useState(false);
  const [sensorTypes, setSensorTypes] = useState<SensorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState<{ kind: 'analog' | 'digital'; index: number } | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [addForm, setAddForm] = useState({
    kind: 'analog' as 'analog' | 'digital',
    index: 0,
    type_key: 'generic_adc',
    name: '',
    calibration: 'none' as Calibration,
    soil_temp_c: '24',
    soil_temp_source: 'open_meteo' as SoilTempSource,
    soil_temp_depth_cm: '18',
    adc_min: '0',
    adc_max: '4095',
    value_min: '0',
    value_max: '100',
    smoothing: '1',
    is_tank: false,
    tank_diameter_mm: '',
    tank_height_mm: '',
  });
  const [saving, setSaving] = useState(false);

  // Apply sensible defaults when the sensor type changes (only for new channels).
  function applyTypeDefaults(typeKey: string) {
    setAddForm(f => {
      // Don't clobber the user's values when editing an existing channel
      if (editingChannel) return { ...f, type_key: typeKey };
      switch (typeKey) {
        case 'temperature':
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'linear',
            adc_min: '0', adc_max: '4095', value_min: '-40', value_max: '125', is_tank: false };
        case 'humidity':
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'linear',
            adc_min: '0', adc_max: '4095', value_min: '0', value_max: '100', is_tank: false };
        case 'water_level':
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'linear',
            adc_min: '0', adc_max: '4095', value_min: '0', value_max: '2000' };
        case 'soil_tension':
          // Watermark firmware reports ohms, so only the Watermark curve applies.
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'watermark', is_tank: false };
        case 'digital_input':
          return { ...f, type_key: typeKey, kind: 'digital', calibration: 'none', is_tank: false };
        default: // generic_adc
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'none', is_tank: false };
      }
    });
  }

  const loadSensor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, types] = await Promise.all([
        espnowService.listSensorsForController(controllerId),
        sensorDevicesService.listTypes(),
      ]);
      setSensorTypes(types);
      setDevices(list);
    } catch {
      setError('Failed to load sensor data');
    } finally {
      setLoading(false);
    }
  }, [controllerId]);

  useEffect(() => { loadSensor(); }, [loadSensor]);

  // The device for the node currently selected. Deriving it rather than storing
  // it keeps the selector and the channel list from drifting apart.
  const sensorData = useMemo(
    () => devices.find(d => (d.field_node_mac ?? null) === selectedMac) ?? null,
    [devices, selectedMac]
  );
  useEffect(() => { setNotSetUp(!loading && !sensorData); }, [loading, sensorData]);

  // Switching nodes must not carry a half-finished channel form across.
  useEffect(() => {
    setShowAddForm(false);
    setEditingChannel(null);
  }, [selectedMac]);

  const selectedNode = sensorNodes.find(n => n.routerMac === selectedMac) ?? null;
  const selectedNodeLabel = selectedNode?.label
    ?? (selectedMac ? selectedMac : 'Master (on-board inputs)');

  async function handleSetup() {
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to set up the sensor device.');
      return;
    }
    setSettingUp(true);
    setError(null);
    try {
      await espnowService.setupSensor(controllerId, selectedMac);
      setSuccess(`Sensor device created for ${selectedNodeLabel}. Now add channels to configure inputs.`);
      await loadSensor();
    } catch {
      setError('Failed to set up sensor device');
    } finally {
      setSettingUp(false);
    }
  }

  async function handleAddChannel() {
    if (!sensorData) return;
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to manage sensor channels.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const config: Record<string, unknown> = {};
      if (addForm.calibration === 'linear') {
        config.calibration = 'linear';
        config.adc_min = Number(addForm.adc_min);
        config.adc_max = Number(addForm.adc_max);
        config.value_min = Number(addForm.value_min);
        config.value_max = Number(addForm.value_max);
      } else if (addForm.calibration === 'watermark') {
        config.calibration = 'watermark';
        config.soil_temp_source = addForm.soil_temp_source;
        if (addForm.soil_temp_source === 'open_meteo') config.soil_temp_depth_cm = Number(addForm.soil_temp_depth_cm);
        const t = Number(addForm.soil_temp_c);
        if (addForm.soil_temp_c.trim() !== '' && Number.isFinite(t)) config.soil_temp_c = t;
      } else {
        config.calibration = 'none';
      }
      const smoothing = Number(addForm.smoothing) || 1;
      if (smoothing > 1) config.smoothing = smoothing;
      if (addForm.type_key === 'water_level' && addForm.is_tank) {
        const d = Number(addForm.tank_diameter_mm);
        const h = Number(addForm.tank_height_mm);
        if (!(d > 0) || !(h > 0)) {
          setError('Tank diameter and height must be positive numbers');
          setSaving(false);
          return;
        }
        config.tank = {
          shape: 'cylinder_vertical',
          diameter_mm: d,
          height_mm: h,
        };
      }
      await espnowService.upsertSensorChannel(
        controllerId,
        addForm.kind,
        addForm.index,
        {
          type_key: addForm.type_key,
          name: addForm.name || null,
          config,
        },
        selectedMac
      );
      setShowAddForm(false);
      setEditingChannel(null);
      if (!editingChannel) {
        setAddForm(f => ({ ...f, name: '', index: f.index + 1 }));
      }
      setSuccess(editingChannel ? 'Channel updated' : 'Channel added');
      await loadSensor();
    } catch {
      setError(editingChannel ? 'Failed to update channel' : 'Failed to add channel');
    } finally {
      setSaving(false);
    }
  }

  function handleEditChannel(ch: SensorChannel) {
    const cfg = ch.config as Record<string, unknown>;
    const isLinear = cfg.calibration === 'linear';
    const isWatermark = cfg.calibration === 'watermark';
    const tank = (cfg.tank as { diameter_mm?: number; height_mm?: number } | undefined) || undefined;
    setAddForm({
      kind: ch.channel_kind as 'analog' | 'digital',
      index: ch.channel_index,
      type_key: ch.type_key,
      name: ch.name || '',
      calibration: isLinear ? 'linear' : isWatermark ? 'watermark' : 'none',
      soil_temp_c: String(cfg.soil_temp_c ?? '24'),
      // A channel saved before this has no source: it was always the typed figure.
      soil_temp_source: cfg.soil_temp_source === 'open_meteo' ? 'open_meteo' : 'fixed',
      soil_temp_depth_cm: String(cfg.soil_temp_depth_cm ?? '18'),
      adc_min: String(cfg.adc_min ?? '0'),
      adc_max: String(cfg.adc_max ?? '4095'),
      value_min: String(cfg.value_min ?? '0'),
      value_max: String(cfg.value_max ?? '100'),
      smoothing: String(cfg.smoothing ?? '1'),
      is_tank: !!tank,
      tank_diameter_mm: tank?.diameter_mm != null ? String(tank.diameter_mm) : '',
      tank_height_mm: tank?.height_mm != null ? String(tank.height_mm) : '',
    });
    setEditingChannel({ kind: ch.channel_kind as 'analog' | 'digital', index: ch.channel_index });
    setShowAdvanced(isLinear || isWatermark || Number(cfg.smoothing ?? 1) > 1);
    setShowAddForm(true);
  }

  async function handleDeleteChannel(kind: 'analog' | 'digital', index: number) {
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to remove sensor channels.');
      return;
    }
    if (!confirm(`Remove ${kind} channel ${index} on ${selectedNodeLabel}?`)) return;
    try {
      await espnowService.deleteSensorChannel(controllerId, kind, index, selectedMac);
      await loadSensor();
    } catch {
      setError('Failed to delete channel');
    }
  }

  function getLatestForChannel(kind: string, index: number) {
    if (!sensorData?.latestReadings) return null;
    return sensorData.latestReadings.find(
      r => r.channel_index === index && r.channel_kind === kind
    ) ?? null;
  }

  if (loading) {
    return (
      <div className="text-sm text-gray-400 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading sensor data…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400">×</button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-400">×</button>
        </div>
      )}

      {/* Node selector — which device in the mesh these channels belong to.
          Each node has its own sensor device, channels and reading history. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="w-4 h-4 text-blue-500" />
            Read sensors from
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {sensorNodes.length === 0 ? (
            <p className="text-sm text-gray-500">
              No nodes reported yet. Once the master and its routers check in they
              will appear here and you can configure each one's inputs.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {sensorNodes.map(node => {
                const dev = devices.find(d => (d.field_node_mac ?? null) === node.routerMac);
                const channelCount = dev?.channels?.length ?? 0;
                const selected = node.routerMac === selectedMac;
                // Capability counts are null on nodes running firmware from
                // before they were advertised — that is "unknown", not "none",
                // so we only call out a node as having no inputs when it said so.
                const knownNoInputs =
                  node.diCount !== null && node.adcCount !== null &&
                  node.diCount === 0 && node.adcCount === 0;
                return (
                  <button
                    key={node.key}
                    type="button"
                    onClick={() => setSelectedMac(node.routerMac)}
                    className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${
                      selected
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          node.online ? 'bg-green-500'
                            // Amber, not red: see the router card. Nothing has
                            // asked this node, so nothing is being claimed.
                            : node.state === 'offline' ? 'bg-red-500' : 'bg-amber-400'
                        }`}
                      />
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {node.label}
                      </span>
                      {node.isLocal && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">
                          master
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                      {node.sublabel}
                    </div>
                    <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                      {channelCount > 0 ? (
                        <span className="text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                          {channelCount} channel{channelCount === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="text-gray-400">not set up</span>
                      )}
                      {node.adcCount !== null && node.adcCount > 0 && (
                        <span className="text-gray-500">{node.adcCount} ADC</span>
                      )}
                      {node.diCount !== null && node.diCount > 0 && (
                        <span className="text-gray-500">{node.diCount} DI</span>
                      )}
                      {knownNoInputs && (
                        <span className="text-gray-400 italic">no sensor inputs</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {notSetUp ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-4">
              <span className="font-medium text-gray-700">{selectedNodeLabel}</span> has
              not been set up as a sensor device yet. Set it up to configure its
              ADC/digital input channels and start recording readings.
            </p>
            <Button onClick={handleSetup} disabled={settingUp}>
              <Plus className="w-4 h-4 mr-1" /> {settingUp ? 'Setting up…' : 'Set Up as Sensor'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Channel list */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2 min-w-0">
                  <Activity className="w-4 h-4 text-violet-500" />
                  Channels ({sensorData?.channels?.length || 0})
                  <span className="text-xs font-normal text-gray-400">
                    on {selectedNodeLabel}
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  {sensorData && (
                    <Link to={`/irrigation/sensors/${encodeURIComponent(sensorData.device_id)}`}>
                      <Button variant="outline" size="sm">View Readings</Button>
                    </Link>
                  )}
                  <button
                    onClick={() => setShowAddForm(f => !f)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Channel
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {showAddForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 space-y-3">
                  {editingChannel && (
                    <div className="text-sm font-medium text-blue-800">
                      Editing {editingChannel.kind} channel {editingChannel.index}
                    </div>
                  )}

                  {/* Primary fields — most users only need these */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs text-gray-700">Sensor Type</span>
                      <select
                        value={addForm.type_key}
                        onChange={e => applyTypeDefaults(e.target.value)}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        {sensorTypes.map(t => (
                          <option key={t.type_key} value={t.type_key}>
                            {t.display_name}{t.unit ? ` (${t.unit})` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-700">Name</span>
                      <input
                        type="text"
                        value={addForm.name}
                        onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Pool Depth"
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-700">Channel</span>
                      <select
                        value={addForm.index}
                        onChange={e => setAddForm(f => ({ ...f, index: Number(e.target.value) }))}
                        disabled={!!editingChannel}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        {/* Only the channels the node reports having; firmware too old to
                            report its counts (null) still gets the full 0-15. */}
                        {Array.from({
                          length: (addForm.kind === 'digital' ? selectedNode?.diCount : selectedNode?.adcCount) ?? 16,
                        }, (_, i) => (
                          <option key={i} value={i}>{addForm.kind === 'digital' ? 'DI' : 'ADC'} {i}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-700">Input Kind</span>
                      <select
                        value={addForm.kind}
                        onChange={e => setAddForm(f => ({ ...f, kind: e.target.value as 'analog' | 'digital' }))}
                        disabled={!!editingChannel}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        <option value="analog">Analog (ADC)</option>
                        <option value="digital">Digital</option>
                      </select>
                    </label>
                  </div>

                  {/* Tank-specific block — shown when type=water_level */}
                  {addForm.type_key === 'water_level' && (
                    <div className="border-t border-blue-200 pt-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-blue-800">
                        <input
                          type="checkbox"
                          checked={addForm.is_tank}
                          onChange={e => setAddForm(f => ({ ...f, is_tank: e.target.checked }))}
                          className="rounded"
                        />
                        Water tank — calculate volume &amp; usage trends
                      </label>
                      {addForm.is_tank && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                          <label className="block">
                            <span className="text-xs text-gray-700">Tank diameter (mm)</span>
                            <input type="number" min="1" step="1"
                              value={addForm.tank_diameter_mm}
                              onChange={e => setAddForm(f => ({ ...f, tank_diameter_mm: e.target.value }))}
                              placeholder="e.g. 1200"
                              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          </label>
                          <label className="block">
                            <span className="text-xs text-gray-700">Tank height (mm)</span>
                            <input type="number" min="1" step="1"
                              value={addForm.tank_height_mm}
                              onChange={e => setAddForm(f => ({ ...f, tank_height_mm: e.target.value }))}
                              placeholder="e.g. 2000"
                              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          </label>
                          <p className="sm:col-span-2 text-xs text-gray-500">
                            Vertical cylindrical tank. Sensor reports fill depth in mm.
                            Capacity ≈ {(() => {
                              const d = Number(addForm.tank_diameter_mm);
                              const h = Number(addForm.tank_height_mm);
                              if (d > 0 && h > 0) return (Math.PI * (d / 2) ** 2 * h / 1_000_000).toFixed(0) + ' L';
                              return '—';
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Advanced / calibration — collapsed by default */}
                  <div className="border-t border-blue-200 pt-3">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(v => !v)}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900"
                    >
                      {showAdvanced ? '▾' : '▸'} Advanced: calibration &amp; smoothing
                    </button>
                    {showAdvanced && (
                      <div className="mt-3 space-y-3">
                        <label className="block">
                          <span className="text-xs text-gray-700">Calibration</span>
                          <select
                            value={addForm.calibration}
                            onChange={e => setAddForm(f => ({ ...f, calibration: e.target.value as Calibration }))}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="none">None (raw value)</option>
                            <option value="linear">Linear (scale ADC to units)</option>
                            <option value="watermark">Watermark soil sensor (ohms → kPa)</option>
                          </select>
                        </label>
                        {addForm.calibration === 'watermark' && (
                          <div className="space-y-2">
                            <label className="block">
                              <span className="text-xs text-gray-700">Soil temperature</span>
                              <select
                                value={addForm.soil_temp_source}
                                onChange={e => setAddForm(f => ({ ...f, soil_temp_source: e.target.value as SoilTempSource }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              >
                                <option value="open_meteo">From Open-Meteo (modelled for the farm)</option>
                                <option value="fixed">A figure I type in</option>
                              </select>
                            </label>
                            {addForm.soil_temp_source === 'open_meteo' && (
                              <label className="block">
                                <span className="text-xs text-gray-700">Depth</span>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <select
                                    value={addForm.soil_temp_depth_cm}
                                    onChange={e => setAddForm(f => ({ ...f, soil_temp_depth_cm: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                  >
                                    {SOIL_TEMP_DEPTHS.map(d => <option key={d.cm} value={d.cm}>{d.label}</option>)}
                                  </select>
                                  <span className="text-xs text-gray-500 min-w-0 flex-1">
                                    Choose the depth nearest the sensor. Modelled at the farm&apos;s centre, so the sensor must be on a farm.
                                  </span>
                                </div>
                              </label>
                            )}
                            <label className="block">
                              <span className="text-xs text-gray-700">
                                {addForm.soil_temp_source === 'open_meteo' ? 'If Open-Meteo cannot be read (°C)' : 'Soil temperature (°C)'}
                              </span>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <input type="number" value={addForm.soil_temp_c}
                                  onChange={e => setAddForm(f => ({ ...f, soil_temp_c: e.target.value }))}
                                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                <span className="text-xs text-gray-500 min-w-0 flex-1">
                                  {addForm.soil_temp_source === 'open_meteo'
                                    ? 'Used only when no modelled figure is available.'
                                    : 'At sensor depth. Readings shift ~1-2% per °C; update it seasonally.'}
                                </span>
                              </div>
                            </label>
                          </div>
                        )}
                        {addForm.calibration === 'linear' && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <label className="block">
                              <span className="text-xs text-gray-700">ADC Min</span>
                              <input type="number" value={addForm.adc_min} onChange={e => setAddForm(f => ({ ...f, adc_min: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                            <label className="block">
                              <span className="text-xs text-gray-700">ADC Max</span>
                              <input type="number" value={addForm.adc_max} onChange={e => setAddForm(f => ({ ...f, adc_max: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                            <label className="block">
                              <span className="text-xs text-gray-700">Value Min</span>
                              <input type="number" value={addForm.value_min} onChange={e => setAddForm(f => ({ ...f, value_min: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                            <label className="block">
                              <span className="text-xs text-gray-700">Value Max</span>
                              <input type="number" value={addForm.value_max} onChange={e => setAddForm(f => ({ ...f, value_max: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                          </div>
                        )}
                        {addForm.kind === 'analog' && (
                          <label className="block">
                            <span className="text-xs text-gray-700">Smoothing window</span>
                            <div className="flex items-center gap-2 mt-1">
                              <input type="number" min="1" max="20" value={addForm.smoothing}
                                onChange={e => setAddForm(f => ({ ...f, smoothing: e.target.value }))}
                                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                              <span className="text-xs text-gray-500">
                                {Number(addForm.smoothing) <= 1
                                  ? 'No smoothing — raw values used directly'
                                  : `Averages the last ${addForm.smoothing} readings`}
                              </span>
                            </div>
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Button onClick={handleAddChannel} disabled={saving} size="sm">
                      {saving
                        ? (editingChannel ? 'Saving…' : 'Adding…')
                        : (editingChannel ? 'Save Changes' : 'Add Channel')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowAddForm(false); setEditingChannel(null); setShowAdvanced(false); }}>Cancel</Button>
                  </div>
                </div>
              )}

              {(!sensorData?.channels || sensorData.channels.length === 0) ? (
                <p className="text-sm text-gray-400 italic">
                  No channels configured. Add a channel to start recording sensor data.
                </p>
              ) : (
                <div className="space-y-2">
                  {sensorData.channels.map(ch => {
                    const latest = getLatestForChannel(ch.channel_kind, ch.channel_index);
                    const cfg = ch.config as Record<string, unknown>;
                    const smoothingVal = Number(cfg.smoothing) || 1;
                    const calLabel = (cfg.calibration === 'linear'
                      ? `Linear ${cfg.adc_min ?? 0}-${cfg.adc_max ?? 4095} → ${cfg.value_min ?? 0}-${cfg.value_max ?? 100}`
                      : cfg.calibration === 'watermark'
                        ? (cfg.soil_temp_source === 'open_meteo'
                          ? `Watermark @ Open-Meteo ${String(cfg.soil_temp_depth_cm ?? 18)} cm`
                          : `Watermark @ ${String(cfg.soil_temp_c ?? 24)} °C`)
                        : 'Raw')
                      + (smoothingVal > 1 ? ` · avg ${smoothingVal}` : '');

                    return (
                      <div key={`${ch.channel_kind}-${ch.channel_index}`}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 flex-wrap"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1 basis-48">
                          {CHANNEL_TYPE_ICONS[ch.type_key] || <Activity className="w-4 h-4 text-gray-400" />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800 break-words">
                              {ch.name || `${ch.channel_kind === 'digital' ? 'DI' : 'ADC'} ${ch.channel_index}`}
                            </div>
                            <div className="text-xs text-gray-500 break-words">
                              {ch.type_display_name}{ch.type_unit ? ` (${ch.type_unit})` : ''} · {ch.channel_kind} ch{ch.channel_index} · {calLabel}
                            </div>
                          </div>
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                          {latest ? (
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-800">
                                {latest.value !== null ? Number(latest.value).toFixed(1) : '—'}
                                {ch.type_unit ? <span className="text-sm font-normal text-gray-500 ml-1">{ch.type_unit}</span> : null}
                              </div>
                              <div className="text-xs text-gray-400">
                                {formatTimeInTz(latest.reading_time, sensorTz)}
                              </div>
                              {cfg.calibration === 'watermark' && soilTempNote(latest.raw) && (
                                <div className="text-xs text-gray-400">{soilTempNote(latest.raw)}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No readings</span>
                          )}
                          <button
                            onClick={() => handleEditChannel(ch)}
                            title="Edit channel"
                            className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteChannel(ch.channel_kind as 'analog' | 'digital', ch.channel_index)}
                            title="Remove channel"
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
